import { createFileRoute, Link } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useMemo, useState } from "react";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { sendTransactionalEmail } from "@/lib/email/send";
import { publicBookingUrl } from "@/lib/booking";
import { Clock, Pencil, RefreshCw, Send, X, Zap } from "lucide-react";

export const Route = createFileRoute("/app/reengagement")({
  head: () => ({
    meta: buildAppMeta({
      title: "Re-engagement — CoachQuick",
      description:
        "Spot clients you haven't seen in a while and send personalized nudges to bring them back.",
      path: "/app/reengagement",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/reengagement` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <ReengagementQueue />
    </RequireAuth>
  ),
});

type Nudge = {
  id: string;
  coach_student_id: string;
  student_email: string;
  student_name: string;
  days_since_last_session: number;
  subject: string;
  body: string;
  status: "pending" | "sent" | "skipped" | "snoozed";
  snoozed_until: string | null;
  sent_at: string | null;
  skipped_at: string | null;
  created_at: string;
};

type Tab = "pending" | "sent" | "snoozed" | "skipped";

const AVATAR_PALETTE = [
  "bg-[#fdba74] text-[#7c2d12]",
  "bg-[#7dd3fc] text-[#075985]",
  "bg-[#9fe870] text-[#054d28]",
  "bg-[#fcd34d] text-[#78350f]",
  "bg-[#f0abfc] text-[#701a75]",
  "bg-[#a5b4fc] text-[#312e81]",
  "bg-[#fda4af] text-[#881337]",
];

function initials(name: string) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

function paletteFor(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

function relativeAgo(iso: string | undefined) {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

function ReengagementQueue() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [scanning, setScanning] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [editing, setEditing] = useState<Nudge | null>(null);
  const [tab, setTab] = useState<Tab>("pending");

  const { data: nudges, isLoading } = useQuery({
    queryKey: ["reengagement-nudges", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reengagement_nudges" as never)
        .select("*")
        .eq("coach_id", user!.id)
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as Nudge[];
    },
  });

  const { data: profile } = useQuery({
    queryKey: ["coach-profile-for-nudge", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from("coach_profile" as never)
        .select("*")
        .eq("coach_id", user!.id)
        .maybeSingle();
      return data as any;
    },
  });

  // The "Book a lesson" link in nudges points at the coach's CoachQuick
  // booking page (when enabled), preferring the friendly slug.
  const { data: bookingSettings } = useQuery({
    queryKey: ["booking-settings-for-nudge", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from("coach_booking_settings")
        .select("enabled, public_token, booking_slug")
        .eq("coach_id", user!.id)
        .maybeSingle();
      return data as {
        enabled: boolean;
        public_token: string;
        booking_slug: string | null;
      } | null;
    },
  });
  const bookingUrl =
    bookingSettings?.enabled && bookingSettings.public_token
      ? publicBookingUrl(bookingSettings.booking_slug || bookingSettings.public_token)
      : null;

  const { data: sessionRows } = useQuery({
    queryKey: ["reengagement-session-counts", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from("sessions")
        .select("coach_student_id")
        .eq("coach_id", user!.id);
      return (data ?? []) as { coach_student_id: string | null }[];
    },
  });

  const priorLessonsByClient = useMemo(() => {
    const m = new Map<string, number>();
    (sessionRows ?? []).forEach((s) => {
      if (!s.coach_student_id) return;
      m.set(s.coach_student_id, (m.get(s.coach_student_id) ?? 0) + 1);
    });
    return m;
  }, [sessionRows]);

  const all = nudges ?? [];
  const pending = all.filter((n) => n.status === "pending");
  const sent = all.filter((n) => n.status === "sent");
  const snoozed = all.filter((n) => n.status === "snoozed");
  const skipped = all.filter((n) => n.status === "skipped");

  const lastScanIso = useMemo(() => {
    return all.reduce<string | undefined>((latest, n) => {
      if (!latest || n.created_at > latest) return n.created_at;
      return latest;
    }, undefined);
  }, [all]);

  async function runScan() {
    setScanning(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const r = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scan-stale-clients`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token ?? ""}`,
          },
          body: "{}",
        },
      );
      if (!r.ok) throw new Error(await r.text());
      const result = await r.json();
      const created = result?.summary?.[0]?.created ?? 0;
      toast.success(
        created > 0
          ? `Found ${created} new ${created === 1 ? "client" : "clients"} to nudge`
          : "All caught up — no stale clients right now",
      );
      qc.invalidateQueries({ queryKey: ["reengagement-nudges"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Scan failed");
    } finally {
      setScanning(false);
    }
  }

  async function approveSend(n: Nudge) {
    try {
      await sendTransactionalEmail({
        templateName: "client-reengagement",
        recipientEmail: n.student_email,
        idempotencyKey: `reengagement-${n.id}`,
        templateData: {
          subject: n.subject,
          body: n.body,
          coachName: profile?.full_name ?? "",
          coachTitle: profile?.title ?? null,
          coachPhone: profile?.phone ?? null,
          coachWebsite: profile?.website ?? null,
          bookingUrl,
          signOff: profile?.sign_off ?? "Thanks!",
        },
      });
      const { error } = await supabase
        .from("reengagement_nudges" as never)
        .update({ status: "sent", sent_at: new Date().toISOString() } as never)
        .eq("id", n.id);
      if (error) throw error;
      toast.success(`Sent to ${n.student_name}`);
      qc.invalidateQueries({ queryKey: ["reengagement-nudges"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Send failed");
    }
  }

  async function sendAll() {
    if (pending.length === 0) return;
    setSendingAll(true);
    try {
      for (const n of pending) {
        await approveSend(n);
      }
    } finally {
      setSendingAll(false);
    }
  }

  async function skip(n: Nudge) {
    const { error } = await supabase
      .from("reengagement_nudges" as never)
      .update({ status: "skipped", skipped_at: new Date().toISOString() } as never)
      .eq("id", n.id);
    if (error) toast.error(error.message);
    else {
      toast.success("Skipped");
      qc.invalidateQueries({ queryKey: ["reengagement-nudges"] });
    }
  }

  async function snooze(n: Nudge) {
    const until = new Date(Date.now() + 7 * 86400000).toISOString();
    const { error } = await supabase
      .from("reengagement_nudges" as never)
      .update({ status: "snoozed", snoozed_until: until } as never)
      .eq("id", n.id);
    if (error) toast.error(error.message);
    else {
      toast.success("Snoozed for 1 week");
      qc.invalidateQueries({ queryKey: ["reengagement-nudges"] });
    }
  }

  async function regenerate(n: Nudge) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/draft-nudge`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: JSON.stringify({
          studentName: n.student_name,
          daysSinceLast: n.days_since_last_session,
          coachName: profile?.full_name ?? null,
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      const drafted = await r.json();
      const { error } = await supabase
        .from("reengagement_nudges" as never)
        .update({ subject: drafted.subject, body: drafted.body } as never)
        .eq("id", n.id);
      if (error) throw error;
      toast.success("Re-drafted");
      qc.invalidateQueries({ queryKey: ["reengagement-nudges"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to regenerate");
    }
  }

  const visible: Nudge[] =
    tab === "pending" ? pending : tab === "sent" ? sent : tab === "snoozed" ? snoozed : skipped;

  return (
    <AppShell>
      <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {pending.length} pending · scanned {relativeAgo(lastScanIso)}
            </p>
            <h1 className="mt-2 font-display text-4xl font-black tracking-tight text-foreground md:text-5xl">
              Re-engage
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Automated notes for clients you haven't seen recently. Nothing sends until you
              approve.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={runScan}
              disabled={scanning}
              className="rounded-[10px]"
            >
              <RefreshCw className={`h-4 w-4 ${scanning ? "animate-spin" : ""}`} />
              {scanning ? "Scanning…" : "Run scan"}
            </Button>
            <Button
              onClick={sendAll}
              disabled={pending.length === 0 || sendingAll}
              className="rounded-[10px]"
            >
              <Zap className="h-4 w-4" />
              {sendingAll ? "Sending…" : `Send all ${pending.length}`}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-5 border-b border-border">
          <div className="flex flex-wrap gap-6 text-sm">
            <TabBtn
              label="Pending"
              count={pending.length}
              active={tab === "pending"}
              onClick={() => setTab("pending")}
            />
            <TabBtn
              label="Sent"
              count={sent.length}
              active={tab === "sent"}
              onClick={() => setTab("sent")}
            />
            <TabBtn
              label="Snoozed"
              count={snoozed.length}
              active={tab === "snoozed"}
              onClick={() => setTab("snoozed")}
            />
            <TabBtn
              label="Skipped"
              count={skipped.length}
              active={tab === "skipped"}
              onClick={() => setTab("skipped")}
            />
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <Card className="rounded-[16px] p-6 shadow-none">
            <Skeleton className="h-32 w-full" />
          </Card>
        ) : visible.length === 0 ? (
          <Card className="rounded-[16px] p-10 text-center shadow-none">
            <p className="text-sm text-muted-foreground">
              {tab === "pending"
                ? "All caught up — no clients need a nudge right now."
                : tab === "sent"
                  ? "Nothing sent yet."
                  : tab === "snoozed"
                    ? "Nothing snoozed."
                    : "Nothing skipped."}
            </p>
          </Card>
        ) : tab === "pending" ? (
          <div className="space-y-4">
            {pending.map((n) => (
              <NudgeCard
                key={n.id}
                nudge={n}
                priorLessons={priorLessonsByClient.get(n.coach_student_id) ?? 0}
                onApprove={() => approveSend(n)}
                onEdit={() => setEditing(n)}
                onRegenerate={() => regenerate(n)}
                onSnooze={() => snooze(n)}
                onSkip={() => skip(n)}
              />
            ))}
          </div>
        ) : (
          <Card className="overflow-hidden rounded-[16px] shadow-none">
            <ul className="divide-y divide-border">
              {visible.map((n) => (
                <li key={n.id} className="flex items-center justify-between gap-3 p-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback
                        className={`text-[10px] font-bold ${paletteFor(n.student_name)}`}
                      >
                        {initials(n.student_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{n.student_name}</p>
                      <p className="truncate text-xs text-muted-foreground">{n.subject}</p>
                    </div>
                  </div>
                  <p className="whitespace-nowrap text-xs text-muted-foreground">
                    {tab === "sent" && n.sent_at && new Date(n.sent_at).toLocaleString()}
                    {tab === "snoozed" &&
                      n.snoozed_until &&
                      `Until ${new Date(n.snoozed_until).toLocaleDateString()}`}
                    {tab === "skipped" &&
                      n.skipped_at &&
                      new Date(n.skipped_at).toLocaleDateString()}
                  </p>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>

      <EditDialog
        nudge={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          qc.invalidateQueries({ queryKey: ["reengagement-nudges"] });
        }}
      />
    </AppShell>
  );
}

function TabBtn({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative -mb-px flex items-center gap-2 border-b-2 pb-3 text-sm font-semibold transition-colors ${
        active
          ? "border-foreground text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
      <span
        className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ${
          active ? "bg-foreground text-background" : "bg-muted text-muted-foreground"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function NudgeCard({
  nudge,
  priorLessons,
  onApprove,
  onEdit,
  onRegenerate,
  onSnooze,
  onSkip,
}: {
  nudge: Nudge;
  priorLessons: number;
  onApprove: () => void;
  onEdit: () => void;
  onRegenerate: () => void;
  onSnooze: () => void;
  onSkip: () => void;
}) {
  return (
    <Card className="rounded-[16px] border bg-card p-[22px] shadow-none">
      <div className="flex items-start gap-3">
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarFallback className={`text-xs font-bold ${paletteFor(nudge.student_name)}`}>
            {initials(nudge.student_name)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/app/clients/$clientId"
              params={{ clientId: nudge.coach_student_id }}
              className="font-semibold hover:underline"
            >
              {nudge.student_name}
            </Link>
            <span className="inline-flex items-center rounded-full bg-[#fff3c2] px-2 py-0.5 text-[11px] font-semibold text-[#b86700]">
              {nudge.days_since_last_session} days quiet
            </span>
            <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              {priorLessons} prior lesson{priorLessons === 1 ? "" : "s"}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{nudge.student_email}</p>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold text-muted-foreground">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#9fe870] opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#9fe870]" />
          </span>
          Automated draft
        </span>
      </div>

      <div className="mt-4 rounded-[12px] border border-border bg-[#e8ebe6] px-[18px] py-4 dark:bg-white/5">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Subject
        </p>
        <p className="mt-1 text-sm font-bold text-foreground">{nudge.subject}</p>
        <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Message
        </p>
        <pre className="mt-1 whitespace-pre-wrap break-words font-sans text-[13.5px] leading-relaxed text-foreground">
          {nudge.body}
        </pre>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={onApprove} className="rounded-[8px]">
          <Send className="h-3.5 w-3.5" /> Approve & send
        </Button>
        <Button size="sm" variant="outline" onClick={onEdit} className="rounded-[8px]">
          <Pencil className="h-3.5 w-3.5" /> Edit
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onRegenerate}
          className="rounded-[8px] text-muted-foreground hover:text-foreground"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Regenerate
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onSnooze}
          className="rounded-[8px] text-muted-foreground hover:text-foreground"
        >
          <Clock className="h-3.5 w-3.5" /> Snooze 1 week
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onSkip}
          className="ml-auto rounded-[8px] text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" /> Skip
        </Button>
      </div>
    </Card>
  );
}

function EditDialog({
  nudge,
  onClose,
  onSaved,
}: {
  nudge: Nudge | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  // Sync state when dialog opens
  if (nudge && subject === "" && body === "") {
    setSubject(nudge.subject);
    setBody(nudge.body);
  }

  async function save() {
    if (!nudge) return;
    setSaving(true);
    const { error } = await supabase
      .from("reengagement_nudges" as never)
      .update({ subject, body } as never)
      .eq("id", nudge.id);
    setSaving(false);
    if (error) toast.error(error.message);
    else {
      toast.success("Saved");
      setSubject("");
      setBody("");
      onSaved();
    }
  }

  function close() {
    setSubject("");
    setBody("");
    onClose();
  }

  return (
    <Dialog open={!!nudge} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit nudge {nudge ? `for ${nudge.student_name}` : ""}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium">Subject</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <label className="text-xs font-medium">Message</label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              className="mt-1.5"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
